import { RudderAnalytics } from "@rudderstack/analytics-js";
import config from "../config";

export const analytics = new RudderAnalytics();

// Attribution is persisted in a cookie scoped to the apex domain (e.g.
// `.helloprenup.com`) rather than `sessionStorage`. This is deliberate:
//  - A `sessionStorage` value written on the marketing site (`helloprenup.com`)
//    is NOT readable on the app subdomain (`app.helloprenup.com`), and is lost
//    when the tab closes. That caused click IDs / UTMs to almost never reach the
//    DB (low attribution match rate).
//  - An apex-domain cookie is shared across all subdomains and survives the hop
//    from the marketing site to the app, plus return visits within its lifetime.
// NOTE: For end-to-end attribution the marketing site must also persist click
// IDs into this same `.{apex}` cookie (or forward the query string onto the
// link into the app) so the app can read them here.
const ATTRIBUTION_COOKIE_KEY = "analytics_last_touch";
const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

let isLoaded = false;

interface Attribution {
  utm_source?: string | undefined;
  utm_medium?: string | undefined;
  utm_campaign?: string | undefined;
  utm_content?: string | undefined;
  utm_term?: string | undefined;
  gclid?: string | undefined;
  gbraid?: string | undefined;
  wbraid?: string | undefined;
  irclid?: string | undefined;
  irpid?: string | undefined;
  fbclid?: string | undefined;
  ttclid?: string | undefined;
  msclkid?: string | undefined;
  ga4_client_id?: string | undefined;
  gad_campaignid?: string | undefined;
  via?: string | undefined;
}

interface PageContext {
  page_path?: string | undefined;
  page_url?: string | undefined;
  referrer?: string | undefined;
}

interface DeviceContext {
  device_type?: "mobile" | "desktop" | undefined;
  browser?: string | undefined;
  os?: string | undefined;
  platform?: string | undefined;
}

type IdentifyTraitValue = string | number | boolean | Date | null | undefined;

type CommonProperties = Attribution &
  PageContext &
  DeviceContext & {
    source: "web";
    session_id: string;
    timestamp: string;
    ga4_session_id?: string;
    ga4_session_number?: string;
  };

const parseAttribution = (search: string): Attribution => {
  if (typeof window === "undefined") return {};

  // Build a case-insensitive lookup so params survive casing/encoding changes
  // introduced by ad networks and redirect chains (e.g. `GCLID`, `UTM_Source`).
  const lookup = new Map<string, string>();
  new URLSearchParams(search).forEach((value, key) => {
    const trimmed = value.trim();
    if (trimmed) lookup.set(key.toLowerCase(), trimmed);
  });
  const getParam = (...keys: string[]) => {
    for (const key of keys) {
      const value = lookup.get(key.toLowerCase());
      if (value) return value;
    }
    return undefined;
  };

  return {
    utm_source: getParam("utm_source"),
    utm_medium: getParam("utm_medium"),
    utm_campaign: getParam("utm_campaign"),
    utm_content: getParam("utm_content"),
    utm_term: getParam("utm_term"),
    gclid: getParam("gclid"),
    gbraid: getParam("gbraid"),
    wbraid: getParam("wbraid"),
    irclid: getParam("irclickid"),
    irpid: getParam("irpid"),
    fbclid: getParam("fbclid"),
    ttclid: getParam("ttclid"),
    msclkid: getParam("msclkid"),
    gad_campaignid: getParam("gad_campaignid"),
    via: getParam("via"),
  };
};

const getGa4ClientId = (): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const rawCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith("_ga="));
  if (!rawCookie) return undefined;

  const rawValue = decodeURIComponent(rawCookie.split("=")[1] ?? "");
  const parts = rawValue.split(".");
  if (parts.length < 4) return undefined;
  const clientId = parts.slice(-2).join(".");
  return clientId || undefined;
};

const getGa4SessionData = (): {
  sessionId?: string;
  sessionNumber?: string;
} => {
  if (typeof document === "undefined") return {};
  const rawCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith("_ga_") && !cookie.startsWith("_ga="));
  if (!rawCookie) return {};

  const rawValue = decodeURIComponent(rawCookie.split("=")[1] ?? "");
  const parts = rawValue.split(".");
  if (parts.length < 4) return {};

  const sessionId = parts[2] ?? undefined;
  const sessionNumber = parts[3] ?? undefined;
  const result: { sessionId?: string; sessionNumber?: string } = {};

  if (sessionId) {
    result.sessionId = sessionId;
  }
  if (sessionNumber) {
    result.sessionNumber = sessionNumber;
  }

  return result;
};

const hasAttribution = (attr: Attribution) =>
  Object.values(attr).some((value) => Boolean(value));

// Resolve the apex domain (e.g. `app.helloprenup.com` -> `.helloprenup.com`) so
// the cookie is shared across the marketing site and the app. Returns undefined
// for `localhost` / IP hosts so the browser falls back to a host-only cookie.
const getCookieDomain = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const hostname = window.location.hostname;
  if (hostname === "localhost" || /^[0-9.]+$/.test(hostname)) return undefined;
  const parts = hostname.split(".");
  if (parts.length < 2) return undefined;
  return `.${parts.slice(-2).join(".")}`;
};

const loadStoredAttribution = (): Attribution => {
  if (typeof document === "undefined") return {};
  const prefix = `${ATTRIBUTION_COOKIE_KEY}=`;
  const rawCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(prefix));
  if (!rawCookie) return {};
  try {
    return JSON.parse(
      decodeURIComponent(rawCookie.slice(prefix.length)),
    ) as Attribution;
  } catch {
    return {};
  }
};

const storeAttribution = (attr: Attribution) => {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(JSON.stringify(attr));
  const domain = getCookieDomain();
  const parts = [
    `${ATTRIBUTION_COOKIE_KEY}=${value}`,
    "path=/",
    `max-age=${ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  if (domain) parts.push(`domain=${domain}`);
  if (window.location.protocol === "https:") parts.push("Secure");
  document.cookie = parts.join("; ");
};

export const getAttribution = (): Attribution => {
  if (typeof window === "undefined") return {};
  // Last-touch semantics: a new attributed landing fully replaces the stored
  // snapshot. We intentionally do NOT merge field-by-field, because click IDs
  // and UTMs are independent fields and merging would let a stale click ID from
  // an earlier touch (e.g. an old `gclid`) ride along with a later, different
  // source (e.g. `?utm_source=newsletter`). The downstream cart write
  // (`buildAttributionLastTouch`) treats every non-empty field as last-touch, so
  // a blended snapshot would attribute purchases to the wrong campaign.
  // When the current URL has no attribution params, we fall back to the stored
  // cookie so the touch survives client-side navigation that strips the query
  // string.
  const latest = parseAttribution(window.location.search);
  if (hasAttribution(latest)) {
    storeAttribution(latest);
    return latest;
  }
  return loadStoredAttribution();
};

// Captures attribution from the current URL into the apex-domain cookie as early
// as possible (app load + on every route change) so click IDs are persisted
// before any client-side navigation can strip the query string.
export const captureAttribution = (): Attribution => getAttribution();

const getPageContext = (): PageContext => {
  if (typeof window === "undefined") return {};
  return {
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer ? document.referrer : undefined,
  };
};

const parseDevice = (): DeviceContext => {
  if (typeof navigator === "undefined") return {};
  const ua = navigator.userAgent || "";
  const isMobile = /Mobi|Android/i.test(ua);
  let browser: string | undefined;
  if (/Edg/i.test(ua)) browser = "edge";
  else if (/Chrome/i.test(ua)) browser = "chrome";
  else if (/Safari/i.test(ua)) browser = "safari";
  else if (/Firefox/i.test(ua)) browser = "firefox";

  let os: string | undefined;
  if (/Windows/i.test(ua)) os = "windows";
  else if (/Mac OS X/i.test(ua)) os = "macos";
  else if (/Android/i.test(ua)) os = "android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "ios";

  const uaData = (navigator as { userAgentData?: { platform?: string } })
    .userAgentData;

  return {
    device_type: isMobile ? "mobile" : "desktop",
    browser,
    os,
    platform: uaData?.platform,
  };
};

const getCommonProperties = (): CommonProperties => {
  const ga4Session = getGa4SessionData();
  const ga4ClientId = getGa4ClientId();
  return {
    source: "web",
    session_id: analytics.getSessionId()?.toString() ?? "",
    timestamp: new Date().toISOString(),
    ...getPageContext(),
    ...parseDevice(),
    ...getAttribution(),
    ...(ga4ClientId ? { ga4_client_id: ga4ClientId } : {}),
    ...(ga4Session.sessionId ? { ga4_session_id: ga4Session.sessionId } : {}),
    ...(ga4Session.sessionNumber
      ? { ga4_session_number: ga4Session.sessionNumber }
      : {}),
  };
};

export const getClientAnalyticsContext = () => {
  const common = getCommonProperties();
  return {
    sessionId: common.session_id,
    pagePath: common.page_path,
    pageUrl: common.page_url,
    referrer: common.referrer,
    utmSource: common.utm_source,
    utmMedium: common.utm_medium,
    utmCampaign: common.utm_campaign,
    utmContent: common.utm_content,
    utmTerm: common.utm_term,
    gclid: common.gclid,
    gbraid: common.gbraid,
    wbraid: common.wbraid,
    irclid: common.irclid,
    irpid: common.irpid,
    fbclid: common.fbclid,
    ttclid: common.ttclid,
    msclkid: common.msclkid,
    ga4ClientId: common.ga4_client_id,
    ga4SessionId: common.ga4_session_id,
    ga4SessionNumber: common.ga4_session_number,
    source: common.source,
    device_type: common.device_type,
    browser: common.browser,
    os: common.os,
    gadCampaignid: common.gad_campaignid,
    via: common.via,
  };
};

export const loadAnalytics = () => {
  if (isLoaded) return;

  analytics.load(config.rudderStackWriteKey, config.rudderStackDataPlaneUrl, {
    autoTrack: {
      enabled: false,
    },
  });
  isLoaded = true;
};

export const identifyUser = (
  userId: string,
  traits?: Record<string, IdentifyTraitValue>,
) => {
  loadAnalytics();

  analytics.identify(userId, traits);
};

export const trackEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => {
  loadAnalytics();
  analytics.track(event, {
    ...getCommonProperties(),
    ...(properties ?? {}),
  });
};

export default analytics;
